import React, { useEffect, useRef } from "react";

import { Cell, Row } from "./styles";

interface CodeInputProps {
    value: string;
    onChange: (value: string) => void;
    // Отдельной кнопки под код нет — отправляем, как только набрана последняя клетка.
    onComplete: (value: string) => void;
    length?: number;
    disabled?: boolean;
    autoFocus?: boolean;
}

const DEFAULT_LENGTH = 4;

export const CodeInput = ({
    value,
    onChange,
    onComplete,
    length = DEFAULT_LENGTH,
    disabled,
    autoFocus,
}: CodeInputProps) => {
    const cellsRef = useRef<Array<HTMLInputElement | null>>([]);

    /*
    * Фокус на первой клетке — и при монтировании, и каждый раз, когда поле снова пусто и
    * разблокировано. ⚠️ Второе обязательно: после неверного кода вызывающий чистит value, а
    * клетки в этот момент были disabled и потеряли фокус — без этого форма выглядит готовой к
    * вводу, но глотает первое нажатие, пока в неё не ткнут мышью.
    * */
    useEffect(() => {
        if (autoFocus && !disabled && value.length === 0) cellsRef.current[0]?.focus();
    }, [autoFocus, disabled, value.length]);

    const applyValue = (next: string) => {
        onChange(next);

        if (next.length === length) onComplete(next);
    };

    /*
    * Клетки — представление одной строки, а не самостоятельные поля: правим её целиком и
    * ставим фокус на первую незаполненную. Иначе пришлось бы держать состояние на каждую
    * клетку и синхронизировать его со вставкой кода из SMS.
    * */
    const handleCellChange = (index: number, raw: string) => {
        const digits = raw.replace(/\D/g, "");

        if (digits.length === 0) return;

        /*
        * Правку клетки в середине накладываем поверх строки, а не обрезаем её: набор «1234»
        * с исправлением второй цифры не должен молча стирать четвёртую, которую не трогали.
        * */
        const next = (
            value.slice(0, index) +
            digits +
            value.slice(index + digits.length)
        ).slice(0, length);

        applyValue(next);
        cellsRef.current[Math.min(index + digits.length, length - 1)]?.focus();
    };

    const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== "Backspace") return;

        event.preventDefault();

        // Пустая клетка отправляет назад: иначе стирать пришлось бы по два нажатия.
        const target = value[index] === undefined ? index - 1 : index;

        if (target < 0) return;

        onChange(value.slice(0, target) + value.slice(target + 1));
        cellsRef.current[target]?.focus();
    };

    return (
        <Row>
            {Array.from({ length }).map((_, index) => (
                <Cell
                    key={index}
                    ref={(element) => {
                        cellsRef.current[index] = element;
                    }}
                    type="tel"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={length}
                    disabled={disabled}
                    value={value[index] ?? ""}
                    onChange={(event) => handleCellChange(index, event.target.value)}
                    onKeyDown={(event) => handleKeyDown(index, event)}
                />
            ))}
        </Row>
    );
};
